import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    CheckCircle2,
    ClipboardList,
    Copy,
    ExternalLink,
    Image as ImageIcon,
    Inbox,
    LinkIcon,
    PlayCircle,
    RefreshCw,
    Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { LoadingState } from "@/components/ui/States";

const PROVIDER_GROUPS = [
    {
        id: "publish-flow",
        title: "1. Submit a test estimate from the public site",
        items: ["submit_real_test_estimate", "lead_appears_in_admin", "lead_created_event"],
    },
    {
        id: "customer-portal",
        title: "2. Customer portal — make sure each customer only sees their own data",
        items: ["generate_customer_portal_link", "portal_isolation_verified"],
    },
    {
        id: "workflow",
        title: "3. Walk a lead through the workflow",
        items: ["move_to_technician_review", "add_estimate_amount", "mark_estimate_sent", "paid_does_not_auto_complete"],
    },
    {
        id: "payment",
        title: "4. Dry-run payment (no real money moves)",
        items: ["create_dry_run_invoice", "mark_invoice_sent", "mark_payment_received", "portal_shows_paid"],
    },
    {
        id: "language",
        title: "5. Public-site language sanity check",
        items: ["no_fake_review_content", "gallery_no_before_after"],
    },
    {
        id: "tools",
        title: "6. Admin tools",
        items: ["automation_log_filters_work"],
    },
    {
        id: "ux",
        title: "7. Honest usability self-check",
        items: ["john_can_walk_workflow_alone"],
    },
    {
        id: "ui",
        title: "8. Home page final eyeball",
        items: ["home_photo_section_loads"],
    },
];

export default function TesterWeek() {
    const [summary, setSummary] = useState(null);
    const [checklist, setChecklist] = useState([]);
    const [seedResult, setSeedResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    const load = async () => {
        const [s, c] = await Promise.all([
            api.get("/admin/tester-week/summary"),
            api.get("/admin/tester-week/checklist"),
        ]);
        setSummary(s.data);
        setChecklist(c.data.items || []);
    };

    useEffect(() => {
        load().finally(() => setLoading(false));
    }, []);

    const itemsByKey = useMemo(() => {
        const map = {};
        for (const it of checklist) map[it.key] = it;
        return map;
    }, [checklist]);

    const seed = async () => {
        setBusy(true);
        setErrorMsg("");
        try {
            const r = await api.post("/admin/tester-week/seed");
            setSeedResult(r.data);
            await load();
        } catch (e) {
            setErrorMsg(e?.response?.data?.detail || "Seeding failed.");
        } finally {
            setBusy(false);
        }
    };

    const reset = async () => {
        if (confirmText !== "DELETE TEST DATA") {
            setErrorMsg("Type DELETE TEST DATA in the confirmation box first.");
            return;
        }
        setBusy(true);
        setErrorMsg("");
        try {
            const r = await api.post("/admin/tester-week/reset", { confirm: confirmText });
            setSeedResult({ reset: r.data });
            setConfirmText("");
            await load();
        } catch (e) {
            setErrorMsg(e?.response?.data?.detail || "Reset failed.");
        } finally {
            setBusy(false);
        }
    };

    const toggleChecklist = async (key, checked) => {
        const item = itemsByKey[key];
        try {
            await api.patch(`/admin/tester-week/checklist/${key}`, {
                checked,
                notes: item?.notes || "",
            });
            await load();
        } catch (e) {
            setErrorMsg(e?.response?.data?.detail || "Couldn't update checklist.");
        }
    };

    const updateChecklistNotes = async (key, notes) => {
        const item = itemsByKey[key];
        try {
            await api.patch(`/admin/tester-week/checklist/${key}`, {
                checked: !!item?.checked,
                notes,
            });
            await load();
        } catch (e) {
            setErrorMsg(e?.response?.data?.detail || "Couldn't save notes.");
        }
    };

    if (loading) return <LoadingState label="Loading Tester Week toolkit…" />;
    const dry = summary?.dry_run_mode;

    return (
        <div data-testid="tester-week-page">
            <h1 className="mb-1">Tester Week Toolkit</h1>
            <p className="text-[var(--color-text-muted)] mb-3 max-w-3xl">
                A safe walk-through of the full A1 Screening workflow before launch — leads, customer portal, payments, automation events. Everything stays inside the dry-run sandbox.
            </p>

            {/* "No real customers" assurance — bold, hard to miss */}
            <div
                className="a1-card mb-5 flex items-start gap-3"
                style={{
                    background: "rgba(40, 216, 224, 0.08)",
                    borderColor: "var(--color-accent)",
                }}
                data-testid="tester-week-no-real-customers"
            >
                <CheckCircle2 size={22} className="text-[var(--color-accent)] shrink-0 mt-0.5" />
                <div>
                    <p className="font-bold m-0 mb-1">No real customers are contacted during tester week.</p>
                    <p className="text-sm m-0 leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                        Test leads use fake phone numbers (<code>+1239 999 980x</code>) and fake email addresses (<code>@testweek.a1screening247.local</code>). Every Stripe / GoHighLevel / Make.com / SMS / email action is captured to the dry-run shadow inbox and the Automation Event Log — nothing leaves the system.
                    </p>
                </div>
            </div>

            {/* Status banner */}
            <DryRunBanner active={dry} />

            {/* Action row */}
            <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="tester-week-actions">
                <div className="a1-card flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <PlayCircle size={18} className="text-[var(--color-accent)]" />
                        <h3 className="text-lg leading-tight">Seed 5 sample customer journeys</h3>
                    </div>
                    <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">
                        Creates 5 dry-run leads in distinct workflow stages so you can walk the admin tools end-to-end. Idempotent — running it again won't double up.
                    </p>
                    <ul className="text-sm text-[var(--color-text-muted)] list-disc pl-5 space-y-1">
                        <li>Small Pool Cage Repair — Technician Review Needed</li>
                        <li>Medium Pool Cage Repair — Estimate Sent</li>
                        <li>Screen Door Replacement — Invoice Sent</li>
                        <li>Screen Door + Z-Bar — Paid / Scheduled</li>
                        <li>Condo Screen Repair — Awaiting Approval</li>
                    </ul>
                    <button
                        onClick={seed}
                        disabled={busy || !dry}
                        className="a1-btn-primary self-start"
                        data-testid="tester-week-seed-btn"
                    >
                        {busy ? <RefreshCw size={16} className="animate-spin" /> : <PlayCircle size={16} />}
                        {busy ? "Seeding…" : "Seed test data"}
                    </button>
                    {!dry ? (
                        <p className="text-xs text-[var(--color-storm)]">
                            Dry-Run Mode is OFF. Re-enable it before seeding.
                        </p>
                    ) : null}
                </div>

                <div className="a1-card flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <Trash2 size={18} className="text-[var(--color-storm)]" />
                        <h3 className="text-lg leading-tight">Delete seeded test data</h3>
                    </div>
                    <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">
                        Removes only records flagged <code>is_test_seed=true</code>. Real customer data is never touched. The action itself is logged to Audit + Automation Event Log.
                    </p>
                    <label className="a1-label">Confirmation</label>
                    <input
                        type="text"
                        className="a1-input"
                        placeholder="Type DELETE TEST DATA"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        data-testid="tester-week-reset-confirm"
                    />
                    <button
                        onClick={reset}
                        disabled={busy || confirmText !== "DELETE TEST DATA"}
                        className="a1-btn-secondary self-start"
                        data-testid="tester-week-reset-btn"
                        style={{ borderColor: "var(--color-storm)", color: "var(--color-storm)" }}
                    >
                        <Trash2 size={16} /> Delete test data
                    </button>
                </div>
            </div>

            {errorMsg ? (
                <div className="a1-card mb-4" style={{ borderColor: "var(--color-storm)" }} data-testid="tester-week-error">
                    <p className="text-sm" style={{ color: "var(--color-storm)" }}>{errorMsg}</p>
                </div>
            ) : null}

            {/* Most-recent seed result (with portal preview links) */}
            {seedResult?.portal_links?.length ? (
                <div className="a1-card mb-6" data-testid="tester-week-portal-links">
                    <h3 className="text-lg mb-3 flex items-center gap-2">
                        <LinkIcon size={18} className="text-[var(--color-accent)]" /> Customer portal preview links (single-use)
                    </h3>
                    <p className="text-xs text-[var(--color-text-muted)] mb-3">
                        Links expire after the configured TTL (30 min) and become invalid after a single redeem. Regenerate from Lead Detail when needed.
                    </p>
                    <div className="grid gap-2">
                        {seedResult.portal_links.map((p) => <PortalLinkRow key={p.lead_id} link={p} />)}
                    </div>
                </div>
            ) : null}
            {seedResult?.reset?.deleted ? (
                <div className="a1-card mb-6">
                    <h3 className="text-lg mb-3">Reset complete</h3>
                    <ul className="text-sm grid grid-cols-2 sm:grid-cols-3 gap-2 text-[var(--color-text-muted)]">
                        {Object.entries(seedResult.reset.deleted).map(([k, v]) => (
                            <li key={k}><strong>{v}</strong> {k.replaceAll("_", " ")} removed</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {/* Summary cards */}
            <h2 className="mb-3">Status</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6" data-testid="tester-week-summary">
                <SummaryCard label="Seeded test leads" value={summary?.seeded_lead_count} testId="card-seeded-leads" />
                <SummaryCard label="Portal preview links" value={summary?.portal_link_count} testId="card-portal-links" />
                <SummaryCard label="Dry-run payment events" value={summary?.payment_event_count} testId="card-payment-events" />
                <SummaryCard label="Dry-run GHL events" value={summary?.ghl_event_count} testId="card-ghl-events" />
                <SummaryCard label="Dry-run Make events" value={summary?.make_event_count} testId="card-make-events" />
                <SummaryCard
                    label="Checklist progress"
                    value={`${summary?.checklist_done || 0}/${summary?.checklist_total || 0}`}
                    testId="card-checklist"
                />
            </div>

            {/* Quick links */}
            <h2 className="mb-3">Quick links</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8" data-testid="tester-week-quicklinks">
                <QuickLink to="/admin/leads" label="Admin Leads" />
                <QuickLink to="/admin/automation-events" label="Automation Event Log" />
                <QuickLink to="/admin/shadow-inbox" label="Shadow Inbox" icon={Inbox} />
                <QuickLink to="/admin/verification" label="Verification Checklist" />
                <QuickLink to="/customer/login" label="Customer Portal Preview" external />
                <QuickLink to="/gallery" label="Public Gallery" external icon={ImageIcon} />
            </div>

            {/* Checklist */}
            <h2 className="mb-3 flex items-center gap-2"><ClipboardList size={20} /> Tester checklist</h2>
            <p className="text-sm text-[var(--color-text-muted)] mb-3">
                Progress saves automatically. Each row records who checked it and when.
            </p>
            <div className="grid gap-4" data-testid="tester-week-checklist">
                {PROVIDER_GROUPS.map((g) => (
                    <ChecklistGroup
                        key={g.id}
                        title={g.title}
                        keys={g.items}
                        itemsByKey={itemsByKey}
                        onToggle={toggleChecklist}
                        onSaveNotes={updateChecklistNotes}
                    />
                ))}
            </div>
        </div>
    );
}

function DryRunBanner({ active }) {
    return (
        <div
            className="a1-card mb-5 flex items-start gap-3"
            style={{
                borderColor: active ? "var(--color-accent)" : "var(--color-storm)",
                background: active ? "rgba(40, 216, 224, 0.06)" : "rgba(231, 119, 78, 0.06)",
            }}
            data-testid="tester-week-dryrun-banner"
        >
            <AlertTriangle size={22} className={active ? "text-[var(--color-accent)]" : "text-[var(--color-storm)]"} />
            <div className="flex-1">
                <p className="font-bold mb-1">
                    {active ? "Dry-Run Mode is ON" : "Dry-Run Mode is OFF"}
                </p>
                <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">
                    {active ? (
                        <>
                            No live SMS, email, payment, webhook, GHL, Make, Stripe, Cloudinary, Plausible, or Sentry calls will be made. Every notification is logged to the dry-run shadow inbox; every Stripe / GHL / Make action returns a mock and writes a row to the Automation Event Log.
                        </>
                    ) : (
                        <>
                            Live integrations are active. Tester Week toolkit is intentionally disabled while live mode is on — re-enable Dry-Run Mode before seeding test data. Real customer notifications could be triggered if you proceed.
                        </>
                    )}
                </p>
            </div>
        </div>
    );
}

function SummaryCard({ label, value, testId }) {
    return (
        <div className="a1-card" data-testid={testId}>
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--color-text-muted)] mb-1">{label}</p>
            <p className="text-3xl serif font-bold">{value === undefined || value === null ? "—" : value}</p>
        </div>
    );
}

function QuickLink({ to, label, external, icon: Icon = ExternalLink }) {
    if (external) {
        return (
            <a
                href={to}
                target="_blank"
                rel="noopener noreferrer"
                className="a1-card a1-card-link no-underline flex items-center gap-2 hover:shadow"
                data-testid={`tester-week-link-${to.replace(/[\/]/g, "-")}`}
            >
                <Icon size={16} className="text-[var(--color-accent)] shrink-0" />
                <span className="text-sm">{label}</span>
            </a>
        );
    }
    return (
        <Link
            to={to}
            className="a1-card a1-card-link no-underline flex items-center gap-2 hover:shadow"
            data-testid={`tester-week-link-${to.replace(/[\/]/g, "-")}`}
        >
            <Icon size={16} className="text-[var(--color-accent)] shrink-0" />
            <span className="text-sm">{label}</span>
        </Link>
    );
}

function PortalLinkRow({ link }) {
    const copy = () => navigator.clipboard?.writeText(link.url);
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-md border" style={{ borderColor: "var(--color-line)", background: "var(--color-bg-card)" }}>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{link.label}</p>
                <p className="text-xs text-[var(--color-text-muted)] truncate"><strong>{link.name}</strong> · {link.url}</p>
            </div>
            <div className="flex gap-2 shrink-0">
                <Link to={`/admin/leads/${link.lead_id}`} className="a1-btn-ghost text-xs">Open lead</Link>
                <button onClick={copy} className="a1-btn-secondary text-xs">
                    <Copy size={12} /> Copy link
                </button>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="a1-btn-ghost text-xs">
                    <ExternalLink size={12} /> Preview
                </a>
            </div>
        </div>
    );
}

function ChecklistGroup({ title, keys, itemsByKey, onToggle, onSaveNotes }) {
    return (
        <div className="a1-card">
            <h3 className="text-base mb-3 uppercase tracking-[0.12em] text-[var(--color-text-muted)]">{title}</h3>
            <div className="grid gap-2">
                {keys.map((key) => {
                    const item = itemsByKey[key];
                    if (!item) return null;
                    return (
                        <ChecklistRow
                            key={item.key}
                            item={item}
                            onToggle={(checked) => onToggle(item.key, checked)}
                            onSaveNotes={(notes) => onSaveNotes(item.key, notes)}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function ChecklistRow({ item, onToggle, onSaveNotes }) {
    const [notes, setNotes] = useState(item.notes || "");
    const [editing, setEditing] = useState(false);

    return (
        <div
            className="flex items-start gap-3 p-3 rounded-md border"
            style={{ borderColor: item.checked ? "var(--color-accent)" : "var(--color-line)" }}
            data-testid={`checklist-row-${item.key}`}
        >
            <button
                onClick={() => onToggle(!item.checked)}
                className="mt-0.5 shrink-0"
                aria-label={item.checked ? "Mark unchecked" : "Mark checked"}
                data-testid={`checklist-toggle-${item.key}`}
            >
                {item.checked ? (
                    <CheckCircle2 size={22} className="text-[var(--color-accent)]" />
                ) : (
                    <span
                        className="block w-[22px] h-[22px] rounded-full border-2"
                        style={{ borderColor: "var(--color-text-muted)" }}
                    />
                )}
            </button>
            <div className="flex-1 min-w-0">
                <p className={`text-sm ${item.checked ? "line-through text-[var(--color-text-muted)]" : ""}`}>
                    {item.label}
                </p>
                {item.checked && item.checked_by ? (
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                        ✓ {item.checked_by} · {new Date(item.checked_at).toLocaleString()}
                    </p>
                ) : null}
                {editing ? (
                    <div className="mt-2 flex gap-2">
                        <input
                            type="text"
                            className="a1-input text-sm flex-1"
                            placeholder="Add a note (optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid={`checklist-notes-input-${item.key}`}
                        />
                        <button
                            onClick={() => { onSaveNotes(notes); setEditing(false); }}
                            className="a1-btn-secondary text-xs"
                            data-testid={`checklist-notes-save-${item.key}`}
                        >
                            Save
                        </button>
                        <button onClick={() => { setNotes(item.notes || ""); setEditing(false); }} className="a1-btn-ghost text-xs">Cancel</button>
                    </div>
                ) : (
                    <div className="mt-1 flex items-center gap-3">
                        {item.notes ? (
                            <p className="text-xs italic text-[var(--color-text-muted)] truncate flex-1">"{item.notes}"</p>
                        ) : null}
                        <button
                            onClick={() => setEditing(true)}
                            className="text-xs underline text-[var(--color-text-muted)] shrink-0"
                            data-testid={`checklist-notes-edit-${item.key}`}
                        >
                            {item.notes ? "Edit note" : "Add note"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
